import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, EMPTY, forkJoin, map, switchMap } from 'rxjs';
import { GlobalConstants } from 'src/app/base/global.constants';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { setAllDataModelTables } from '../actions/datamodel.actions';
import { setPropertiesValues } from '../actions/propertypane.actions';
import { setRoles } from '../actions/roles.actions';
import { setIdMapping } from '../actions/screens.actions';
import { setFields } from '../actions/services.actions';
import {
  deleteWorkflow,
  deleteWorkflowInterim,
  deleteWorkflowInterimFailure,
  deleteWorkflowInterimSuccess,
  deleteWorkflowMail,
  getWorkflowData,
  getWorkflowInterimData,
  getWorkflowInterims,
  getWorkflowMail,
  getWorkflows,
  getWorkflowsWithSearch,
  getWorkflowsWithSorting,
  saveWorkflow,
  saveWorkflowMail,
  saveWorkflowOrderFailure,
  saveWorkflowOrderSuccess,
  saveWorkflowStepsOrder,
  setAllActions,
  setAllActors,
  setWorkflowData,
  setWorkflowDT,
  setWorkflowMail,
  setWorkflows,
  setWorkflowsWithSorting
} from '../actions/workflow.actions';
import { ApiConstants } from 'src/app/base/api-constants';
import { UtilService } from 'src/app/services/util.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class WorkFlowEffects {
  private actions$ = inject(Actions);
  private baseService = inject(HttpBaseService);
  private store = inject(Store);
  private router = inject(Router);
  private utilService = inject(UtilService);
  private translateService = inject(TranslateService);

  getWorkflowData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getWorkflowData),
        map((action: any) => {
          return combineLatest([
            this.baseService[action.apiConstants?.roles.method](
              action.apiConstants.roles,
              GlobalConstants.dataTableHeaders
            ),
            this.baseService[action.apiConstants?.workflow.method](action.apiConstants?.workflow, action.sid),
            this.baseService[action.apiConstants?.fields.method](action.apiConstants?.fields, action.params),
            this.baseService[action.apiConstants?.actorsData.method](
              action.apiConstants?.actorsData,
              action.workflowId
            ),
            this.baseService[action.apiConstants?.actionsData.method](
              action.apiConstants?.actionsData,
              action.workflowId
            )
          ]).subscribe((res: any) => {
            this.store.dispatch(
              setRoles({
                rolesTable: res[0]
              })
            );
            this.store.dispatch(
              setWorkflowDT({
                workflowDT: res[1]
              })
            );
            this.store.dispatch(
              setFields({
                fields: res[2]
              })
            );
            this.store.dispatch(
              setAllActors({
                wfActors: res[3]
              })
            );
            this.store.dispatch(
              setAllActions({
                wfActions: res[4]
              })
            );
          });
        })
      ),
    {
      dispatch: false
    }
  );

  getWorkflowInterimData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getWorkflowInterimData),
        map((action: any) => {

          return forkJoin({
            workflowInterim: this.baseService[action.apiConstants?.workflow.method](action.apiConstants?.workflow, action.params),

            roles: this.baseService[action.apiConstants?.roles.method](
              action.apiConstants.roles, action.rolesPayload),

            fields: this.baseService[action.apiConstants?.fields.method](action.apiConstants?.fields, action.params)
          }).subscribe((res: any) => {

            this.store.dispatch(
              setRoles({
                rolesTable: res.roles
              })
            );
            this.store.dispatch(
              setWorkflowDT({
                workflowDT: res.workflowInterim
              })
            );
            this.store.dispatch(
              setFields({
                fields: res.fields
              })
            );
            this.store.dispatch(
              setAllActors({
                wfActors: res.workflowInterim.actorSteps
              })
            );
            this.store.dispatch(
              setAllActions({
                wfActions: res.workflowInterim.actionSteps
              })
            );
          });
        })
      ),
    {
      dispatch: false
    }
  );

  getWorkflows$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getWorkflows),
        map((action: any) => {
          return combineLatest([
            this.baseService[action.apiConstants?.tables.method](
              action.apiConstants.tables,
              GlobalConstants.dataTableHeaders
            ),
            this.baseService[action.apiConstants?.workflow.method](action.apiConstants?.workflow)
          ]).subscribe((res: any) => {
            this.store.dispatch(
              setAllDataModelTables({
                allDataModelTables: res[0]
              })
            );
            this.store.dispatch(
              setWorkflows({
                workflows: res[1]
              })
            );
          });
        })
      ),
    {
      dispatch: false
    }
  );

  getWorkflowInterims$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getWorkflowInterims),
        map((action: any) => {
          return forkJoin({
            workflows: this.baseService[action.apiConstants?.workflow.method](action.apiConstants?.workflow, action.params),
            dataModels: this.baseService[action.apiConstants?.dataModels.method](action.apiConstants?.dataModels, action.paginationRequestPayload)
          }).subscribe((res: any) => {

			this.store.dispatch(
              setAllDataModelTables({
                allDataModelTables: res?.dataModels?.results || []
              })
            );

            let workflows = res?.workflows || [];
            this.store.dispatch(
              setWorkflows({
                workflows: workflows
              })
            );
          })
        })
      ),
    {
      dispatch: false
    }
  );
  getWorkflowsWithSorting$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getWorkflowsWithSorting),
        map((action) =>
          this.baseService[action.apiConstants?.method](action.apiConstants, action.params).subscribe((result: any) => {
            this.store.dispatch(
              setWorkflows({
                workflows: result
              })
            );
          })
        )
      ),
    {
      dispatch: false
    }
  );

  getWorkflowsWithSearch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getWorkflowsWithSearch),
        map((action) => {
          const searchQuery = {
            start: 0,
            length: 10,
            search: action.searchFields
          };
          this.baseService[action.apiConstants?.method](action.apiConstants, JSON.stringify(searchQuery)).subscribe(
            (result: any) => {
              this.store.dispatch(
                setWorkflows({
                  workflows: result.results
                })
              );
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

  saveWorkflow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveWorkflow),
        map((action) =>
          this.baseService[action.apiConstants?.method](action.apiConstants, action.workflowReqData).subscribe(
            (result: any) => {
              if (result) {
                if (action.apiConstants?.method === 'post') {
                  if (result.workflow) {
                    const orgId = result.workflow?.orgId;
                    const appId = result.workflow?.appId;
                    this.router
                      .navigate([
                        `/home/${orgId}/${appId}/designs/viewWorkflow`,
                        result.workflow?.sid,
                        result.workflow?.tableId
                      ])
                      .catch((e) => {
                        console.error(e);
                      });
                  }
                } else {
                  const apiConstants = {
                    tables: {
                      ...ApiConstants.getAllTables
                    },
                    tableFields: {
                      ...ApiConstants.getApplicationDataModelMetaInfo
                    },
                    workflow: {
                      ...ApiConstants.getWorkflows
                    }
                  };
                  this.store.dispatch(getWorkflows({ apiConstants }));
                }
              }
            }
          )
        )
      ),
    { dispatch: false }
  );

  deleteWorkflow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteWorkflow),
        map((action) => this.baseService[action.apiConstants?.method](action.apiConstants, action.ids).subscribe())
      ),
    { dispatch: false }
  );

  saveWorkflowStepsOrder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveWorkflowStepsOrder),
        map((action) => {
          this.store.dispatch(saveWorkflowOrderSuccess({ results: null }));
          this.store.dispatch(saveWorkflowOrderFailure({ error: null }));
          this.baseService[action.apiConstants?.method](action.apiConstants, action.workflowReqData).subscribe(
            (results: any) => {
              this.store.dispatch(saveWorkflowOrderSuccess({ results: results }));
            },
            (err: any) => {
              this.store.dispatch(saveWorkflowOrderFailure({ error: err }));
            }
          );
        })
      ),
    { dispatch: false }
  );

  getWorkflowMail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getWorkflowMail),
        map((action: any) =>
          this.baseService[action.apiConstants?.method](action.apiConstants, action.params).subscribe((res: any) => {
            if (res) {
              this.store.dispatch(
                setWorkflowMail({
                  workflowMail: res
                })
              );
            } else {
              this.store.dispatch(
                setWorkflowMail({
                  workflowMail: { sid: null }
                })
              );
            }
          })
        )
      ),
    {
      dispatch: false
    }
  );

  saveWorkflowMail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveWorkflowMail),
        map((action) =>
          this.baseService[action.apiConstants?.method](action.apiConstants, action.workflowMailData).subscribe()
        )
      ),
    { dispatch: false }
  );

  deleteWorkflowMail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteWorkflowMail),
        map((action) => this.baseService[action.apiConstants?.method](action.apiConstants, action.ids).subscribe())
      ),
    { dispatch: false }
  );

  deleteWorkflowInterim$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteWorkflowInterim),
        map((action) => this.baseService[action.apiConstants?.method](action.apiConstants,action.params).subscribe({
          next: (res: any) => {
            this.store.dispatch(deleteWorkflowInterimSuccess({ workflowId: action.params.workflowId }));
          },
          error: (error: any) => {
            this.store.dispatch(deleteWorkflowInterimFailure({ error }));
          }
        }))
      ),
    { dispatch: false }
  );

  deleteWorkflowInterimSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteWorkflowInterimSuccess),
        map((action: any) => {
          this.utilService.showSuccess(this.translateService.instant('COMPONENT_DELETED_SUCCESSFULLY', { component: 'Workflow' }) as string);
          return EMPTY;
        })
      ),
    { dispatch: false }
  );

  deleteWorkflowInterimFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteWorkflowInterimFailure),
        map((action: any) => {
          return EMPTY;
        })
      ),
    { dispatch: false }
  );
}
